/** Filter controls + saved-view presets for the GIS triage queue (GIS Phase
 *  2A, Task 10, spec §6). Every control composes into one `FindingsQuery`
 *  (`GET /gis-queue/findings`'s query params) and is reported up via
 *  `onFiltersChange` — this component holds no fetch/pagination state of its
 *  own, matching the `SecondaryReviewDashboard`/`DiscrepancyDashboard` idiom
 *  of a dumb filter strip driven by the parent page.
 *
 *  Presets encode the brief's three saved views EXACTLY:
 *  - "Attribute wave": `type=attribute_changed&platform_value_null=true` —
 *    the PURE-FILLS scope. The `platform_value_null=true` flag is load
 *    bearing: without it a bulk sweep off this preset would also catch the
 *    ~3k genuine value-to-value overwrites, not just the ~30k NULL fills.
 *  - "Relationship conflicts": seeds `type=relationship_conflict`. The
 *    findings endpoint accepts exactly one `type` value (no IN-list), so the
 *    label names exactly what this preset filters to rather than the whole
 *    relationship_* family (task-10 review M2 — the generic "Relationships"
 *    label overstated its scope); the ordinary type dropdown below already
 *    lists the other two relationship types for a one-click pivot, and the
 *    counts_by_type chips surface their non-zero counts alongside this
 *    preset for an operator who wants the wider family.
 *  - "Needs decision": clears every filter — the full pending queue, the
 *    catch-all/reset view.
 *
 *  The `platform_value_null` toggle is explicitly three-state (any/null/set)
 *  per the brief, never a plain boolean checkbox. */

import {
  DISCREPANCY_TYPES,
  DISCREPANCY_TYPE_LABELS,
  TABLE_NAMES,
  type DiscrepancyType,
  type FindingsQuery,
  type TableName,
} from './types.ts'

export type PresetKey = 'attribute_wave' | 'relationships' | 'needs_decision'

export const PRESET_LABELS: Record<PresetKey, string> = {
  attribute_wave: 'Attribute wave',
  relationships: 'Relationship conflicts',
  needs_decision: 'Needs decision',
}

export const PRESET_QUERIES: Record<PresetKey, FindingsQuery> = {
  attribute_wave: { type: 'attribute_changed', platform_value_null: true },
  relationships: { type: 'relationship_conflict' },
  needs_decision: {},
}

const PRESET_KEYS: PresetKey[] = ['attribute_wave', 'relationships', 'needs_decision']

type PlatformValueNullOption = 'any' | 'null' | 'set'

function toOption(value: boolean | undefined): PlatformValueNullOption {
  if (value === true) return 'null'
  if (value === false) return 'set'
  return 'any'
}

function fromOption(option: string): boolean | undefined {
  if (option === 'null') return true
  if (option === 'set') return false
  return undefined
}

interface FilterBarProps {
  filters: FindingsQuery
  onFiltersChange: (next: FindingsQuery) => void
  activePreset: PresetKey | null
  onPresetSelect: (preset: PresetKey) => void
}

export function FilterBar({ filters, onFiltersChange, activePreset, onPresetSelect }: FilterBarProps) {
  function patch(next: Partial<FindingsQuery>) {
    onFiltersChange({ ...filters, ...next })
  }

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap gap-2" role="group" aria-label="Saved views">
        {PRESET_KEYS.map(key => (
          <button
            key={key}
            type="button"
            onClick={() => onPresetSelect(key)}
            aria-pressed={activePreset === key}
            className={`text-xs px-3 py-1.5 rounded-full border transition-colors ${
              activePreset === key
                ? 'bg-blue-600 border-blue-600 text-white'
                : 'bg-white border-gray-300 text-gray-700 hover:bg-gray-50'
            }`}
          >
            {PRESET_LABELS[key]}
          </button>
        ))}
      </div>

      <div className="flex flex-wrap items-end gap-3">
        <label className="flex flex-col text-xs text-gray-600 gap-1">
          Type
          <select
            value={filters.type ?? ''}
            onChange={e =>
              patch({ type: e.target.value ? (e.target.value as DiscrepancyType) : undefined })
            }
            className="text-sm border border-gray-300 rounded px-2 py-1 bg-white text-gray-700"
          >
            <option value="">Any type</option>
            {DISCREPANCY_TYPES.map(type => (
              <option key={type} value={type}>
                {DISCREPANCY_TYPE_LABELS[type]}
              </option>
            ))}
          </select>
        </label>

        <label className="flex flex-col text-xs text-gray-600 gap-1">
          Table
          <select
            value={filters.table ?? ''}
            onChange={e => patch({ table: e.target.value ? (e.target.value as TableName) : undefined })}
            className="text-sm border border-gray-300 rounded px-2 py-1 bg-white text-gray-700"
          >
            <option value="">Any table</option>
            {TABLE_NAMES.map(table => (
              <option key={table} value={table}>
                {table}
              </option>
            ))}
          </select>
        </label>

        <label className="flex flex-col text-xs text-gray-600 gap-1">
          Feeder ID
          <input
            type="number"
            min={1}
            value={filters.feeder_id ?? ''}
            onChange={e => patch({ feeder_id: e.target.value ? Number(e.target.value) : undefined })}
            className="w-24 text-sm border border-gray-300 rounded px-2 py-1"
          />
        </label>

        <label className="flex flex-col text-xs text-gray-600 gap-1">
          Run ID
          <input
            type="number"
            min={1}
            value={filters.run_id ?? ''}
            onChange={e => patch({ run_id: e.target.value ? Number(e.target.value) : undefined })}
            className="w-24 text-sm border border-gray-300 rounded px-2 py-1"
          />
        </label>

        <label className="flex flex-col text-xs text-gray-600 gap-1">
          Min age (days)
          <input
            type="number"
            min={0}
            value={filters.min_age_days ?? ''}
            onChange={e => patch({ min_age_days: e.target.value ? Number(e.target.value) : undefined })}
            className="w-24 text-sm border border-gray-300 rounded px-2 py-1"
          />
        </label>

        <label className="flex flex-col text-xs text-gray-600 gap-1">
          Platform value
          <select
            value={toOption(filters.platform_value_null)}
            onChange={e => patch({ platform_value_null: fromOption(e.target.value) })}
            className="text-sm border border-gray-300 rounded px-2 py-1 bg-white text-gray-700"
          >
            <option value="any">Any</option>
            <option value="null">Empty only (fills)</option>
            <option value="set">Has a value (overwrites)</option>
          </select>
        </label>
      </div>
    </div>
  )
}
